import React, { useState, useEffect } from 'react';
import { submitFormToFirebase } from '../../services/firebaseService';
import SkillsAutocomplete from './SkillsAutocomplete';
import { fieldsOfStudy } from '../../data/fieldsOfStudy';
import { WHATSAPP_GROUP_LINK } from '../../config/constants';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  Container,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
} from '@mui/material';
import {
  Person,
  School,
  Work,
  Info,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import Logo from '../../assets/logo.png';

interface FormData {
  // Personal Information
  name: string;
  birthYear: string;
  emiratesOfResidence: string;
  referredBy: string;
  phoneNumber: string;
  linkedinAccount: string;
  email: string;
  
  // Academic Information
  educationLevel: string;
  fieldOfStudy: string;
  otherFieldOfStudy: string;
  coursesCompleted: string;
  universityName: string;
  
  // Professional Information
  currentPosition: string;
  companyName: string;
  lookingForJob: boolean;
  professionalJourney: string;
  communityHelpedEmployment: string;
  skills: string[];
  
  // Additional Information
  interestedInSpeaking: string;
  speakingTopics: string;
  interestedInEntrepreneurship: string;
  hasProjectIdea: string;
  projectIdeaDescription: string;
  interestedInVolunteering: string;
  volunteerTeam: string;
  generalNote: string;
}

interface FormErrors {
  name?: string;
  birthYear?: string;
  emiratesOfResidence?: string;
  referredBy?: string;
  phoneNumber?: string;
  linkedinAccount?: string;
  email?: string;
  educationLevel?: string;
  fieldOfStudy?: string;
  otherFieldOfStudy?: string;
  coursesCompleted?: string;
  universityName?: string;
  currentPosition?: string;
  companyName?: string;
  lookingForJob?: string;
  professionalJourney?: string;
  communityHelpedEmployment?: string;
  skills?: string;
  interestedInSpeaking?: string;
  speakingTopics?: string;
  interestedInEntrepreneurship?: string;
  hasProjectIdea?: string;
  projectIdeaDescription?: string;
  interestedInVolunteering?: string;
  volunteerTeam?: string;
  generalNote?: string;
}

const SyrDevUaeForm: React.FC = () => {
  const mobileFriendlySelectLabelSx = {
    '& .MuiInputLabel-root': {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
      lineHeight: 1.2,
      maxWidth: 'calc(100% - 24px)',
    },
  };

  // Load saved form data from localStorage
  const getSavedFormData = (): FormData => {
    const saved = localStorage.getItem('syrdevuae-form-data');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure skills array exists for backward compatibility
        if (!parsed.skills) {
          parsed.skills = [];
        }
        return parsed;
      } catch (e) {
        console.warn('Failed to parse saved form data');
      }
    }
    return {
      name: '',
      birthYear: '',
      emiratesOfResidence: '',
      referredBy: '',
      phoneNumber: '',
      linkedinAccount: '',
      email: '',
      educationLevel: '',
      fieldOfStudy: '',
      otherFieldOfStudy: '',
      coursesCompleted: '',
      universityName: '',
      currentPosition: '',
      companyName: '',
      lookingForJob: false,
      professionalJourney: '',
      communityHelpedEmployment: '',
      skills: [],
      interestedInSpeaking: '',
      speakingTopics: '',
      interestedInEntrepreneurship: '',
      hasProjectIdea: '',
      projectIdeaDescription: '',
      interestedInVolunteering: '',
      volunteerTeam: '',
      generalNote: '',
    };
  };

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(getSavedFormData);
  const [isInitialized, setIsInitialized] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Initialize form data on component mount
  useEffect(() => {
    if (!isInitialized) {
      const savedData = getSavedFormData();
      console.log('Initializing form with data:', savedData);
      setFormData(savedData);
      setIsInitialized(true);
    }
  }, [isInitialized]);

  const steps = [
    'Personal Information',
    'Academic Information', 
    'Professional Information',
    'Additional Information'
  ];

  const handleInputChange = (field: keyof FormData, value: string | string[] | boolean) => {
    setFormData(prev => {
      const newFormData = { ...prev, [field]: value };
      // Save to localStorage
      localStorage.setItem('syrdevuae-form-data', JSON.stringify(newFormData));
      return newFormData;
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 0:
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        // Birth year is optional (validation not needed since it's a dropdown with valid years)
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
        else if (!/^[+]?[1-9][\d]{0,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
          newErrors.phoneNumber = 'Please enter a valid phone number';
        }
        if (!formData.linkedinAccount.trim()) newErrors.linkedinAccount = 'LinkedIn account is required';
        else if (!/^https?:\/\/(www\.)?linkedin\.com\/in\/[\w\-_À-ÿ%]+\/?$/.test(formData.linkedinAccount)) {
          newErrors.linkedinAccount = 'Please enter a valid LinkedIn profile URL';
        }
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
        if (!formData.referredBy.trim()) newErrors.referredBy = 'Please tell us who referred you to our community';
        if (!formData.emiratesOfResidence.trim()) newErrors.emiratesOfResidence = 'Emirates of residence is required';
        break;
      case 1:
        if (!formData.educationLevel.trim()) newErrors.educationLevel = 'Education level is required';
        if (!formData.fieldOfStudy.trim()) newErrors.fieldOfStudy = 'Field of study is required';
        if (!formData.universityName.trim()) newErrors.universityName = 'University name is required';
        break;
      case 2:
        // Professional journey is now optional
        if (!formData.skills || formData.skills.length < 3) {
          newErrors.skills = 'Please select at least 3 technical skills';
        }
        if (!formData.communityHelpedEmployment.trim()) {
          newErrors.communityHelpedEmployment = 'Please answer if the community helped you obtain income or employment';
        }
        break;
      case 3:
        if (!formData.interestedInSpeaking.trim()) {
          newErrors.interestedInSpeaking = 'Please answer if you would like to be a speaker';
        }
        if (formData.interestedInSpeaking === 'Yes' && !formData.speakingTopics.trim()) {
          newErrors.speakingTopics = 'Please specify the topics you would like to speak about';
        }
        if (!formData.interestedInEntrepreneurship.trim()) {
          newErrors.interestedInEntrepreneurship = 'Please answer if you are interested in entrepreneurship';
        }
        if (!formData.hasProjectIdea.trim()) {
          newErrors.hasProjectIdea = 'Please answer if you have a project or idea that requires support';
        }
        if (!formData.interestedInVolunteering.trim()) {
          newErrors.interestedInVolunteering = 'Please answer if you are interested in volunteering';
        }
        if (formData.interestedInVolunteering === 'Yes' && !formData.volunteerTeam.trim()) {
          newErrors.volunteerTeam = 'Please select a team where you would like to contribute';
        }
        // generalNote remains optional
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (validateStep(activeStep)) {
      setIsSubmitting(true);
      setSubmitError('');
      
      try {
        // Submit to Firebase
        const submissionId = await submitFormToFirebase(formData);
        console.log('Form submitted successfully with ID:', submissionId);
        
        // Clear saved form data after successful submission
        localStorage.removeItem('syrdevuae-form-data');
        
        setShowSuccess(true);
        
        // Reset form data but keep success message visible
        const emptyFormData = getSavedFormData();
        setFormData(emptyFormData);
        setActiveStep(0);
        setErrors({});
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError(error instanceof Error ? error.message : 'Failed to submit form. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const renderPersonalInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
          Personal Information
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <TextField
            fullWidth
            label="Full Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            autoComplete="name"
          />
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <FormControl fullWidth error={!!errors.birthYear} sx={mobileFriendlySelectLabelSx}>
            <InputLabel>Birth Year</InputLabel>
            <Select
              value={formData.birthYear}
              label="Birth Year"
              onChange={(e) => handleInputChange('birthYear', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {Array.from({ length: (new Date().getFullYear() - 18) - 1949 }, (_, i) => {
                const year = (new Date().getFullYear() - 18) - i;
                return (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.birthYear && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.birthYear}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <FormControl fullWidth error={!!errors.emiratesOfResidence} required sx={mobileFriendlySelectLabelSx}>
            <InputLabel>Emirates of Residence</InputLabel>
            <Select
              value={formData.emiratesOfResidence}
              label="Emirates of Residence"
              onChange={(e) => handleInputChange('emiratesOfResidence', e.target.value)}
            >
              <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
              <MenuItem value="Dubai">Dubai</MenuItem>
              <MenuItem value="Sharjah">Sharjah</MenuItem>
              <MenuItem value="Ajman">Ajman</MenuItem>
              <MenuItem value="Umm Al Quwain">Umm Al Quwain</MenuItem>
              <MenuItem value="Ras Al Khaimah">Ras Al Khaimah</MenuItem>
              <MenuItem value="Fujairah">Fujairah</MenuItem>
            </Select>
            {errors.emiratesOfResidence && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.emiratesOfResidence}
              </Typography>
            )}
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <TextField
            fullWidth
            label="Who referred you to our community?"
            value={formData.referredBy}
            onChange={(e) => handleInputChange('referredBy', e.target.value)}
            error={!!errors.referredBy}
            helperText={errors.referredBy || "Please enter the name of the person or source"}
            placeholder="Name of person or source"
            required
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber || "Include country code if international"}
            placeholder="+971 50 123 4567"
            required
            autoComplete="tel"
          />
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <TextField
            fullWidth
            label="LinkedIn Account"
            value={formData.linkedinAccount}
            onChange={(e) => handleInputChange('linkedinAccount', e.target.value)}
            error={!!errors.linkedinAccount}
            helperText={errors.linkedinAccount || "Enter your full LinkedIn profile URL"}
            placeholder="https://linkedin.com/in/yourprofile"
            required
          />
        </Box>
      </Box>
      <Box>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          required
          autoComplete="email"
        />
      </Box>
    </Box>
  );

  const renderAcademicInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          <School sx={{ mr: 1, verticalAlign: 'middle' }} />
          Academic Information
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <FormControl fullWidth error={!!errors.educationLevel} required sx={mobileFriendlySelectLabelSx}>
            <InputLabel>Education Level</InputLabel>
            <Select
              value={formData.educationLevel}
              label="Education Level"
              onChange={(e) => handleInputChange('educationLevel', e.target.value)}
            >
              <MenuItem value="PHD">PhD</MenuItem>
              <MenuItem value="Master Degree">Master Degree</MenuItem>
              <MenuItem value="Bachelor">Bachelor</MenuItem>
              <MenuItem value="University Student">University Student</MenuItem>
              <MenuItem value="School Student">School Student</MenuItem>
            </Select>
            {errors.educationLevel && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.educationLevel}
              </Typography>
            )}
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <FormControl fullWidth error={!!errors.fieldOfStudy} required sx={mobileFriendlySelectLabelSx}>
            <InputLabel>Field of Study</InputLabel>
            <Select
              value={formData.fieldOfStudy}
              label="Field of Study"
              onChange={(e) => {
                handleInputChange('fieldOfStudy', e.target.value);
                // Clear otherFieldOfStudy if not selecting "Other"
                if (e.target.value !== 'Other (Please specify)') {
                  handleInputChange('otherFieldOfStudy', '');
                }
              }}
            >
              {fieldsOfStudy.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </Select>
            {errors.fieldOfStudy && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.fieldOfStudy}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>
      {formData.fieldOfStudy === 'Other (Please specify)' && (
        <Box>
          <TextField
            fullWidth
            label="Please specify your field of study"
            value={formData.otherFieldOfStudy}
            onChange={(e) => handleInputChange('otherFieldOfStudy', e.target.value)}
            error={!!errors.otherFieldOfStudy}
            helperText={errors.otherFieldOfStudy || "Optional"}
            placeholder="Enter your field of study"
          />
        </Box>
      )}
      <Box>
        <TextField
          fullWidth
          label="University/Institute Name"
          value={formData.universityName}
          onChange={(e) => handleInputChange('universityName', e.target.value)}
          error={!!errors.universityName}
          helperText={errors.universityName}
          placeholder="Last university or institute you studied at"
          required
        />
      </Box>
      <Box>
        <TextField
          fullWidth
          label="Courses You Completed"
          value={formData.coursesCompleted}
          onChange={(e) => handleInputChange('coursesCompleted', e.target.value)}
          multiline
          rows={4}
          placeholder="List the courses you have completed..."
        />
      </Box>
    </Box>
  );

  const renderProfessionalInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          <Work sx={{ mr: 1, verticalAlign: 'middle' }} />
          Professional Information
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <TextField
            fullWidth
            label="Current Position"
            value={formData.currentPosition}
            onChange={(e) => handleInputChange('currentPosition', e.target.value)}
          />
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <TextField
            fullWidth
            label="Company Name"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
          />
        </Box>
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.lookingForJob}
              onChange={(e) => handleInputChange('lookingForJob', e.target.checked)}
              color="primary"
            />
          }
          label="Currently looking for a job"
        />
      </Box>
      <Box>
        <TextField
          fullWidth
          label="Professional Journey"
          value={formData.professionalJourney}
          onChange={(e) => handleInputChange('professionalJourney', e.target.value)}
          multiline
          rows={4}
          error={!!errors.professionalJourney}
          helperText={errors.professionalJourney || "Tell us about your previous roles, companies, and years of experience (Optional)"}
        />
      </Box>
      <Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Has the community helped you obtain income or employment?
        </Typography>
        <FormControl fullWidth error={!!errors.communityHelpedEmployment} required>
          <InputLabel>Select an option</InputLabel>
          <Select
            value={formData.communityHelpedEmployment}
            label="Select an option"
            onChange={(e) => handleInputChange('communityHelpedEmployment', e.target.value)}
          >
            <MenuItem value="Yes - Income">Yes - Income</MenuItem>
            <MenuItem value="Yes - Employment">Yes - Employment</MenuItem>
            <MenuItem value="Yes - Both">Yes - Both</MenuItem>
            <MenuItem value="No">No</MenuItem>
            <MenuItem value="Not Applicable">Not Applicable</MenuItem>
          </Select>
          {errors.communityHelpedEmployment && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors.communityHelpedEmployment}
            </Typography>
          )}
        </FormControl>
      </Box>
      
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Technical Skills & Technologies
        </Typography>
        <SkillsAutocomplete
          value={formData.skills || []}
          onChange={(skills) => handleInputChange('skills', skills)}
          error={!!errors.skills}
          helperText={errors.skills}
        />
      </Box>
    </Box>
  );

  const renderGeneralInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
          Additional Information
        </Typography>
      </Box>
      <Box>
        <FormControl fullWidth error={!!errors.interestedInSpeaking} required sx={mobileFriendlySelectLabelSx}>
          <InputLabel>Would you like to be a speaker at our events?</InputLabel>
          <Select
            value={formData.interestedInSpeaking}
            label="Would you like to be a speaker at our events?"
            onChange={(e) => {
              handleInputChange('interestedInSpeaking', e.target.value);
              // Clear speakingTopics if not selecting "Yes"
              if (e.target.value !== 'Yes') {
                handleInputChange('speakingTopics', '');
              }
            }}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
          {errors.interestedInSpeaking && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors.interestedInSpeaking}
            </Typography>
          )}
        </FormControl>
      </Box>
      {formData.interestedInSpeaking === 'Yes' && (
        <Box>
          <TextField
            fullWidth
            label="Which topics would you prefer to speak about?"
            value={formData.speakingTopics}
            onChange={(e) => handleInputChange('speakingTopics', e.target.value)}
            multiline
            rows={3}
            error={!!errors.speakingTopics}
            helperText={errors.speakingTopics}
            placeholder="Please specify the topics you would like to speak about..."
            required
          />
        </Box>
      )}
      <Box>
        <FormControl fullWidth error={!!errors.interestedInEntrepreneurship} required sx={mobileFriendlySelectLabelSx}>
          <InputLabel>Are you interested in entrepreneurship?</InputLabel>
          <Select
            value={formData.interestedInEntrepreneurship}
            label="Are you interested in entrepreneurship?"
            onChange={(e) => handleInputChange('interestedInEntrepreneurship', e.target.value)}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
            <MenuItem value="Maybe">Maybe</MenuItem>
          </Select>
          {errors.interestedInEntrepreneurship && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors.interestedInEntrepreneurship}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Box>
        <FormControl fullWidth error={!!errors.hasProjectIdea} required sx={mobileFriendlySelectLabelSx}>
          <InputLabel>Do you currently have a project or idea that requires support?</InputLabel>
          <Select
            value={formData.hasProjectIdea}
            label="Do you currently have a project or idea that requires support?"
            onChange={(e) => {
              handleInputChange('hasProjectIdea', e.target.value);
              // Clear projectIdeaDescription if not selecting "Yes"
              if (e.target.value !== 'Yes') {
                handleInputChange('projectIdeaDescription', '');
              }
            }}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
          {errors.hasProjectIdea && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors.hasProjectIdea}
            </Typography>
          )}
        </FormControl>
      </Box>
      {formData.hasProjectIdea === 'Yes' && (
        <Box>
          <TextField
            fullWidth
            label="Please describe your project or idea (Optional)"
            value={formData.projectIdeaDescription}
            onChange={(e) => handleInputChange('projectIdeaDescription', e.target.value)}
            multiline
            rows={4}
            placeholder="Describe your project or idea that requires support..."
          />
        </Box>
      )}
      <Box>
        <FormControl fullWidth error={!!errors.interestedInVolunteering} required sx={mobileFriendlySelectLabelSx}>
          <InputLabel>Are you interested in volunteering with SyrDevUAE?</InputLabel>
          <Select
            value={formData.interestedInVolunteering}
            label="Are you interested in volunteering with SyrDevUAE?"
            onChange={(e) => {
              handleInputChange('interestedInVolunteering', e.target.value);
              // Clear volunteerTeam if not selecting "Yes"
              if (e.target.value !== 'Yes') {
                handleInputChange('volunteerTeam', '');
              }
            }}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
          {errors.interestedInVolunteering && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors.interestedInVolunteering}
            </Typography>
          )}
        </FormControl>
      </Box>
      {formData.interestedInVolunteering === 'Yes' && (
        <Box>
          <FormControl fullWidth error={!!errors.volunteerTeam} required sx={mobileFriendlySelectLabelSx}>
            <InputLabel>If yes, please choose the team where you would like to contribute:</InputLabel>
            <Select
              value={formData.volunteerTeam}
              label="If yes, please choose the team where you would like to contribute:"
              onChange={(e) => handleInputChange('volunteerTeam', e.target.value)}
            >
              <MenuItem value="Technical Team">Technical Team</MenuItem>
              <MenuItem value="Organizing & Events Team">Organizing & Events Team</MenuItem>
              <MenuItem value="Design & Creative Team">Design & Creative Team</MenuItem>
              <MenuItem value="Social Media & Content Support Team">Social Media & Content Support Team</MenuItem>
            </Select>
            {errors.volunteerTeam && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.volunteerTeam}
              </Typography>
            )}
          </FormControl>
        </Box>
      )}
      <Box>
        <TextField
          fullWidth
          label="General Note"
          value={formData.generalNote}
          onChange={(e) => handleInputChange('generalNote', e.target.value)}
          multiline
          rows={4}
          placeholder="Any additional notes or comments..."
        />
      </Box>
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderAcademicInfo();
      case 2:
        return renderProfessionalInfo();
      case 3:
        return renderGeneralInfo();
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        {/* <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}>
          <CheckCircle fontSize="large" />
        </Avatar> */}
        <img src={Logo} alt="SyrDevUae Logo" style={{ width: 120, marginBottom: 24, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        <Typography variant="h3" component="h1" gutterBottom color="primary">
          SyrDevUae
        </Typography>
        {/* <Typography variant="h5" color="text.secondary" gutterBottom>
          Elite Membership Knowledge Center
        </Typography> */}
        <Typography variant="body1" color="text.secondary">
          Join our community of developers and innovators
        </Typography>
      </Box>
      

      {showSuccess ? (
        /* Success Page */
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" component="h2" gutterBottom color="primary" fontWeight={700}>
              Application Submitted Successfully!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, mt: 2 }}>
              Welcome to SyrDevUae Community!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Join our WhatsApp group to connect with the community and stay updated with upcoming events and opportunities.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="large"
              href={WHATSAPP_GROUP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Join Our Community on WhatsApp
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stepper */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {/* <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Progress: {Math.round(((activeStep + 1) / steps.length) * 100)}% Complete
              </Typography>
            </Box> */}
          </Paper>

          {/* Form */}
          <Card elevation={3}>
            <CardContent sx={{ p: 4 }}>
              <Box key={`step-${activeStep}`}>
                {renderStepContent(activeStep)}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Box>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear all form data?')) {
                        localStorage.removeItem('syrdevuae-form-data');
                        setFormData(getSavedFormData());
                        setActiveStep(0);
                        setErrors({});
                      }
                    }}
                    variant="outlined"
                    color="error"
                  >
                    Clear Form
                  </Button>
                </Box>
                <Box>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      endIcon={isSubmitting ? null : <Send />}
                      size="large"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      size="large"
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Error Message */}
      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={() => setSubmitError('')}
      >
        <Alert onClose={() => setSubmitError('')} severity="error" sx={{ width: '100%' }}>
          {submitError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SyrDevUaeForm; 